import { useState } from "react";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { Button } from "@/lib/components/Button/Button";
import { Input } from "@/lib/components/Input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { cn } from "@/lib/utils";

const AUDIO_TAG_GROUPS = [
  {
    label: "Pause",
    tags: ["short pause", "pause", "long pause"],
  },
  {
    label: "Tone",
    tags: [
      "warm",
      "friendly",
      "confident",
      "calm",
      "upbeat",
      "excited",
      "sophisticated",
      "inviting",
    ],
  },
  {
    label: "Reaction",
    tags: ["impressed", "in awe", "delighted", "curious", "proud"],
  },
  {
    label: "Pace & delivery",
    tags: [
      "slowly",
      "quickly",
      "softly",
      "whispering",
      "emphasized",
      "smiling",
      "conversational",
    ],
  },
  {
    label: "Vocalizations",
    tags: [
      "light chuckle",
      "laughing",
      "contented sigh",
      "deep breath",
      "gasping",
    ],
  },
];

type Props = {
  onSelect: (tag: string) => void;
  className?: string;
};

export function AudioTagPicker({ onSelect, className }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const query = search.replace(/[[\]]/g, "").trim().toLowerCase();
  const groups = AUDIO_TAG_GROUPS.map((group) => ({
    ...group,
    tags: group.tags.filter((tag) => tag.includes(query)),
  })).filter((group) => group.tags.length > 0);

  const addTag = (tag: string) => {
    onSelect(tag);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) setSearch("");
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              size="iconSm"
              variant="quaternary"
              type="button"
              className={cn("w-7", className)}
            >
              <IconChevronDown />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Add Tag</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" className="w-80 p-3">
        <Input
          containerSize="sm"
          placeholder="Add tag"
          leadingComponent={<IconPlus size={18} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && query) {
              e.preventDefault();
              addTag(query);
            }
          }}
        />
        <div className="mt-3 flex max-h-70 flex-col gap-3 overflow-y-auto">
          {query && (
            <button
              type="button"
              className="flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-2 text-left text-sm whitespace-nowrap hover:bg-tertiary-100"
              onClick={() => addTag(query)}
            >
              <IconPlus size={16} className="shrink-0" />
              <span className="min-w-0 truncate">
                Add <span className="font-semibold">[{query}]</span>
              </span>
            </button>
          )}
          {groups.map((group) => (
            <div key={group.label} className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                {group.label}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {group.tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="flex h-6 cursor-pointer items-center rounded-md border border-tertiary-400/5 bg-tertiary-100 px-2 text-xs font-semibold text-tertiary-400 hover:bg-tertiary-150"
                    onClick={() => addTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
